import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { formatRelativeTime } from '@/lib/formatRelativeTime'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Pill, type PillTone } from '@/components/ui/Pill'
import { StatCard } from '@/components/ui/StatCard'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import type { DashboardProps, DashboardEvent, Onboarding } from './types'

// --- Local dashboard icons (domain vocabulary) ---
const iconBase = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const KegGlyphIcon = () => (
  <svg {...iconBase} className="h-5 w-5">
    <path d="M8.5 3h7l1 3.2c.75 2.2.75 9.4 0 11.6L15.5 21h-7l-1-3.2c-.75-2.2-.75-9.4 0-11.6L8.5 3Z" />
    <path d="M7.6 8.4h8.8M7.6 15.6h8.8" />
  </svg>
)
const StockIcon = () => (
  <svg {...iconBase} className="h-5 w-5">
    <path d="M3 10.5 12 4l9 6.5" />
    <path d="M5 9.5V20h14V9.5" />
    <path d="M9.5 20v-5h5v5" />
  </svg>
)
const ClientIcon = () => (
  <svg {...iconBase} className="h-5 w-5">
    <path d="M3 6.5h10v9H3z" />
    <path d="M13 9.5h4l3 3v3h-7z" />
    <circle cx="7" cy="17.5" r="1.6" />
    <circle cx="17" cy="17.5" r="1.6" />
  </svg>
)
const WarningIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg {...iconBase} strokeWidth={1.9} className={className}>
    <path d="M12 3.5 21 19H3L12 3.5Z" />
    <path d="M12 10v4.5" />
    <circle cx="12" cy="17.4" r="0.4" fill="currentColor" stroke="none" />
  </svg>
)

function CheckDot({ done }: { done: boolean }) {
  if (!done) return <span className="h-6 w-6 shrink-0 rounded-full border-2 border-line" />
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-leaf text-paper">
      <svg viewBox="0 0 20 20" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={3}>
        <path d="M4 10l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

function OnboardingChecklist({ onboarding }: { onboarding: Onboarding }) {
  const pct = Math.round((onboarding.completed_count / onboarding.total) * 100)
  return (
    <Card className="mb-8 p-6">
      <h2 className="font-display text-2xl text-ink">{onboarding.title}</h2>
      <p className="mt-1 text-sm text-ink-soft">{onboarding.subtitle}</p>

      <div className="mt-4 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-paper-2">
          <div
            className="h-full rounded-full bg-flame transition-[width] duration-500 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="shrink-0 text-xs font-bold text-ink-soft">{onboarding.progress_label}</span>
      </div>

      <ul className="mt-5 space-y-3">
        {onboarding.steps.map((step) => (
          <li key={step.key} className="flex items-center gap-3">
            <CheckDot done={step.done} />
            <span className={`text-sm ${step.done ? 'text-ink-faint line-through' : 'text-ink'}`}>{step.label}</span>
            {step.cta && (
              <Button href={step.cta.href} className="ml-auto shrink-0 px-3.5 py-1.5 text-sm">
                {step.cta.label}
              </Button>
            )}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => router.post('/onboarding_dismissal')}
        className="mt-5 text-xs text-ink-faint hover:text-ink-soft"
      >
        {onboarding.dismiss_label}
      </button>
    </Card>
  )
}

function WelcomeBanner({ message, closeLabel }: { message: string; closeLabel: string }) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null
  return (
    <div className="mb-6 flex items-start justify-between gap-4 rounded-xl bg-mustard-tint px-4 py-3">
      <p className="text-sm text-mustard-deep">{message}</p>
      <button
        onClick={() => setDismissed(true)}
        aria-label={closeLabel}
        className="shrink-0 text-lg leading-none text-mustard-deep hover:text-ink"
      >
        ×
      </button>
    </div>
  )
}

function StatStrip({ stats, labels }: Pick<DashboardProps, 'stats' | 'labels'>) {
  return (
    <>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard tone="ink" icon={<KegGlyphIcon />} value={stats.total} label={labels.stats.total} />
        <StatCard tone="leaf" icon={<StockIcon />} value={stats.in_stock} label={labels.stats.in_stock} />
        <StatCard tone="mustard" icon={<ClientIcon />} value={stats.delivered} label={labels.stats.delivered} />
        <StatCard tone="flame" emphasis icon={<WarningIcon />} value={stats.at_risk} label={labels.stats.at_risk} />
      </div>
      {stats.total > 0 && <p className="mt-3 text-sm text-ink-faint">{labels.stats.estimated_value_caption}</p>}
    </>
  )
}

// Twice the AT_RISK_AFTER threshold (30 days) → "critical"; otherwise "high".
const CRITICAL_DAYS = 60

function AtRiskSection({
  atRiskKegs,
  labels,
}: {
  atRiskKegs: DashboardProps['at_risk_kegs']
  labels: DashboardProps['labels']['at_risk']
}) {
  return (
    <section className="mt-10">
      <SectionHeader title={labels.heading} sketch viewAll={{ label: labels.view_all, href: '/kegs' }} />
      <p className="mb-4 text-sm text-ink-soft">{labels.subheading}</p>

      {atRiskKegs.length === 0 ? (
        <EmptyState>{labels.empty}</EmptyState>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-3 border-b border-line bg-flame-tint/40 px-5 py-3.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-flame-tint text-flame-deep">
              <WarningIcon className="h-[18px] w-[18px]" />
            </span>
            <span className="text-sm font-extrabold text-ink">{labels.summary}</span>
            <span className="ml-auto text-sm font-extrabold text-flame-deep">{labels.value_caption}</span>
          </div>
          <ul>
            {atRiskKegs.map((keg) => {
              const critical = keg.days_out >= CRITICAL_DAYS
              return (
                <li key={keg.identifier}>
                  <Link
                    href={`/kegs/${keg.identifier}`}
                    className="flex items-center gap-3 border-t border-line px-5 py-3.5 first:border-t-0 hover:bg-paper-2"
                  >
                    <Pill tone={critical ? 'flame' : 'mustard'} className="min-w-[72px]">
                      {critical ? labels.severity.critical : labels.severity.high}
                    </Pill>
                    <span className="w-20 shrink-0 font-extrabold text-ink">{keg.identifier}</span>
                    <span className="flex-1 truncate text-sm text-ink-soft">
                      {keg.current_beer ?? '—'} · {keg.current_location ?? '—'}
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-flame-deep">
                      {keg.days_out_label}
                    </span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </Card>
      )}
    </section>
  )
}

const EVENT_TONE: Record<DashboardEvent['event_type'], PillTone> = {
  delivery: 'mustard',
  pickup: 'leaf',
  washing: 'leaf',
  incident: 'flame',
  state_change: 'ink',
}

function ActivitySection({
  events,
  labels,
}: {
  events: DashboardProps['recent_events']
  labels: DashboardProps['labels']['activity']
}) {
  return (
    <section className="mt-10">
      <SectionHeader title={labels.heading} viewAll={{ label: labels.view_all, href: '/kegs' }} />

      {events.length === 0 ? (
        <EmptyState>{labels.empty}</EmptyState>
      ) : (
        <Card className="overflow-hidden">
          <ul>
            {events.map((event) => (
              <li key={event.id}>
                <Link
                  href={`/kegs/${event.keg_identifier}`}
                  className="flex items-center gap-3 border-t border-line px-5 py-3.5 text-sm first:border-t-0 hover:bg-paper-2"
                >
                  <Pill tone={EVENT_TONE[event.event_type]} className="min-w-[96px]">
                    {event.event_type_label}
                  </Pill>
                  <span className="font-extrabold text-ink">{event.keg_identifier}</span>
                  {event.location && <span className="truncate text-ink-soft">{event.location}</span>}
                  <span className="ml-auto shrink-0 whitespace-nowrap text-ink-faint">
                    {formatRelativeTime(event.created_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  )
}

export default function DashboardShow({
  stats,
  at_risk_kegs,
  recent_events,
  welcome_message,
  onboarding,
  labels,
}: DashboardProps) {
  // While onboarding is active and no kegs exist yet, the guide stands alone:
  // a wall of zeros would only be noise. Once kegs are created, the real
  // dashboard appears beneath the guide so progress feels tangible.
  const showDashboard = !onboarding || stats.total > 0

  return (
    <>
      <Head title={labels.title} />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        {welcome_message && <WelcomeBanner message={welcome_message} closeLabel={labels.close} />}
        {onboarding && <OnboardingChecklist onboarding={onboarding} />}

        {showDashboard && (
          <>
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="text-xs font-extrabold uppercase tracking-[0.1em] text-ink-faint">{labels.greeting}</div>
                <h1 className="mt-1.5 font-display text-4xl text-ink">{labels.title}</h1>
              </div>
              <Button href="/kegs/batch/new" className="shrink-0">
                ＋ {labels.create_kegs}
              </Button>
            </div>

            <div className="mt-6">
              <StatStrip stats={stats} labels={labels} />
            </div>

            <AtRiskSection atRiskKegs={at_risk_kegs} labels={labels.at_risk} />
            <ActivitySection events={recent_events} labels={labels.activity} />
          </>
        )}
      </div>
    </>
  )
}
